import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';

  const currentCategory = categories.find((c) => c.slug === currentSlug);
  const categoryName = currentCategory?.name || '全部';

  const filteredComics =
    currentSlug === 'all'
      ? comics
      : comics.filter((c) => c.category === currentSlug);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://36manhua.com' },
      {
        '@type': 'ListItem',
        position: 2,
        name: `${categoryName}漫画`,
        item: `https://36manhua.com/category/${currentSlug}`,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画`}
        description={`36漫画网${categoryName}漫画专区，提供海量${categoryName}类漫画全集在线免费阅读。每日更新，高清画质，畅享${categoryName}漫画阅读体验。`}
        keywords={`${categoryName}漫画,洞洞杂货铺,在线免费阅读,36漫画网`}
        canonicalUrl={`https://36manhua.com/category/${currentSlug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: `${categoryName}漫画` }]} />

            <div className="mb-6">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
                {categoryName}漫画
              </h1>
              <p className="text-sm text-foreground-600">
                共 {filteredComics.length} 部{categoryName === '全部' ? '' : categoryName}漫画
              </p>
            </div>

            <div className="bg-secondary-100 rounded-lg p-2 mb-8">
              <div className="flex flex-wrap gap-1">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-md text-sm font-label whitespace-nowrap cursor-pointer transition-colors ${
                      currentSlug === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-700 hover:bg-secondary-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5 pb-12">
                {filteredComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg mb-3 aspect-[5/7]">
                      <img
                        src={comic.coverImage}
                        alt={comic.title}
                        title={`${comic.title} - ${comic.author}`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-label bg-accent-500 text-background-50 whitespace-nowrap">
                        {comic.rating}
                      </div>
                      {comic.status === '已完结' && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-label bg-foreground-800/70 text-background-50 whitespace-nowrap">
                          已完结
                        </div>
                      )}
                    </div>
                    <h3 className="text-sm md:text-base font-heading text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {comic.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-500 whitespace-nowrap">{comic.author}</span>
                      <span className="text-xs text-accent-600 bg-accent-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                        {comic.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">暂无该分类的漫画，去看看其他分类吧</p>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-1 mt-4 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-label whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  <i className="ri-grid-line"></i>浏览全部漫画
                </Link>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}